import { CommandProps, Editor, Extension, RawCommands } from "@tiptap/core";
import { Node } from "@tiptap/pm/model";
import { EditorState, Plugin, PluginKey, Transaction } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import { getEditorInstanceId } from "@/editor/utils/editor";

import "./styles.css";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    bookmark: {
      /**
       * Toggle a bookmark
       * @param nodeId The node ID of the bookmark
       * @example editor.commands.toggleBookmark('nodeId')
       */
      toggleBookmark: (nodeId: string) => ReturnType;
    };
  }
}

export interface BookmarkOptions {
  /**
   * Node types to apply the bookmark to
   * @default []
   */
  nodeTypes: string[];
}

/**
 * This extension adds the visual interactive bookmark button to nodes that have been marked as already bookmarked in the document.
 */
export const BookmarkExtension = Extension.create<BookmarkOptions>({
  name: "bookmark",

  addOptions() {
    return {
      nodeTypes: [],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.nodeTypes,
        attributes: {
          bookmark: {
            default: null,
            parseHTML: element => element.getAttribute(`bookmark`),
            renderHTML: attributes => {
              if (!attributes["bookmark"]) {
                return {};
              }

              return {
                bookmark: attributes["bookmark"],
                class: "bookmarked-node",
              };
            },
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    const bookmarkPluginKey = new PluginKey("bookmark");
    const editor = this.editor;

    return [
      new Plugin<{ decorations: DecorationSet }>({
        key: bookmarkPluginKey,

        state: {
          init() {
            return {
              decorations: DecorationSet.empty,
            };
          },

          apply(transaction, oldState) {
            const metadata = transaction.getMeta(bookmarkPluginKey);

            if (metadata && metadata.decorations) {
              return { decorations: metadata.decorations };
            }

            if (hasBookmarkAttributeChanged(transaction)) {
              return { decorations: createBookmarkDecorations(transaction.doc, editor) };
            }

            return {
              decorations: oldState.decorations.map(transaction.mapping, transaction.doc),
            };
          },
        },

        props: {
          decorations(state) {
            return this.getState(state)?.decorations;
          },
        },

        view: editorView => {
          const decorations = createBookmarkDecorations(editorView.state.doc, editor);
          editorView.dispatch(
            editorView.state.tr.setMeta(bookmarkPluginKey, {
              decorations,
            }),
          );

          return {
            update(view, prevState) {
              if (view.state.doc !== prevState.doc) {
                const currentDecorations = bookmarkPluginKey.getState(view.state)?.decorations;
                const newDecorations = createBookmarkDecorations(view.state.doc, editor);

                // Only dispatch if decorations actually changed
                if (!decorationsEqual(currentDecorations, newDecorations)) {
                  view.dispatch(
                    view.state.tr.setMeta(bookmarkPluginKey, {
                      decorations: newDecorations,
                    }),
                  );
                }
              }
            },
          };
        },
      }),
    ];

    /**
     * Compares two DecorationSet objects to check if they are equivalent
     * @param set1 - First decoration set
     * @param set2 - Second decoration set
     * @returns True if the decoration sets are equivalent
     */
    function decorationsEqual(set1: DecorationSet | undefined, set2: DecorationSet): boolean {
      if (!set1) return false;

      return set1.find().length === set2.find().length;
    }

    /**
     * Checks if a bookmark attribute has been changed in the transaction
     * @param transaction - The transaction to check
     * @returns True if a bookmark attribute changed, false otherwise
     */
    function hasBookmarkAttributeChanged(transaction: Transaction): boolean {
      // Check if this transaction is from a toggleBookmark command
      if (transaction.getMeta("toggleBookmark")) {
        return true;
      }

      return false;
    }
  },

  addCommands() {
    return {
      toggleBookmark:
        (nodeId: string) =>
        ({ state, chain }: CommandProps): boolean => {
          const nodePos = getNode(nodeId, state);
          if (!nodePos) {
            return false;
          }

          const hasBookmark = nodePos.node.attrs.bookmark;

          chain()
            .command(({ tr }) => {
              tr.setNodeMarkup(nodePos.pos, undefined, {
                ...nodePos.node.attrs,
                bookmark: hasBookmark == null ? true : null,
              });

              // Mark this transaction as a bookmark toggle
              tr.setMeta("toggleBookmark", true);
              return true;
            })
            .run();

          return true;
        },
    } as Partial<RawCommands>;
  },
});

/**
 * Creates a set of decorations for all bookmarked nodes in the document
 * @param doc - The document to create decorations for
 * @param editor - The editor instance
 * @returns A DecorationSet containing all bookmark decorations
 */
function createBookmarkDecorations(doc: Node, editor: Editor): DecorationSet {
  const decorations: Decoration[] = [];

  const editorInstanceId = getEditorInstanceId(editor);

  doc.descendants((node, pos) => {
    if (node.attrs.bookmark) {
      const nodeId = node.attrs["node-id"];
      if (!nodeId) return;

      const nodeIdElement = editor.view.dom.querySelector(`[node-id="${nodeId}"]`);
      if (!nodeIdElement) return;

      decorations.push(
        Decoration.widget(
          pos + 1,
          () => {
            const button = document.createElement("nw-bookmark-btn");
            button.setAttribute("node-id", nodeId);
            button.setAttribute("editor-instance-id", editorInstanceId);
            button.setAttribute("active", "true");
            button.className = "bookmark-btn-decoration";
            return button;
          },
          {
            key: `bookmark-${nodeId}`,
            side: -1,
          },
        ),
      );

      decorations.push(
        Decoration.node(pos, pos + node.nodeSize, {
          class: "bookmarked-node",
        }),
      );
    }
  });

  return DecorationSet.create(doc, decorations);
}

/**
 * Retrieves a node from the document based on its node ID.
 * @param nodeId - The ID of the node to retrieve.
 * @param state - The current editor state.
 * @returns An object containing the position and node if found, or null if not found.
 */
function getNode(nodeId: string, state: EditorState): { pos: number; node: Node } | null {
  let nodePos: { pos: number; node: Node } | null = null;

  state.doc.descendants((node, pos) => {
    if (node.attrs["node-id"] === nodeId) {
      nodePos = { pos, node };
    }
  });

  return nodePos;
}
